import React, { lazy, Suspense, useCallback } from 'react';
import { Navigate, Route, Routes as RouterRoutes } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  APPLICATION_ROLES, QUIZ_EDITION_ROLES, ROUTES, TRANSLATION_ROLES, VALIDATOR_ROLES
} from 'utils/constants';
import { Header, SkeletonMain } from 'components';
import { userStore } from 'stores';
import { KeyCloakUtils, UserHelper } from 'utils';
import { CookiesHelper } from 'utils/helpers/CookiesHelper';

const PageHome = lazy(() => import('pages/PageHome'));
const PageAdministration = lazy(() => import('pages/PageAdministration'));
const PageAdministrationQuiz = lazy(() => import('pages/PageAdministrationQuiz'));
const PageAdministrationTranslations = lazy(() => import('pages/PageAdministrationTranslations'));
const PageAdministrationTranslationList = lazy(() => import('pages/PageAdministrationTranslationList'));
const PageAdministrationLanguages = lazy(() => import('pages/PageAdministrationLanguages'));
const PageAdministrationCountry = lazy(() => import('pages/PageAdministrationCountry'));
const PageCountry = lazy(() => import('pages/PageCountry'));
const PageQuiz = lazy(() => import('pages/PageQuiz'));
const PageQuizResults = lazy(() => import('pages/PageQuizResults'));
const PageRGPD = lazy(() => import('pages/PageRGPD'));
const PageStructureLevelTree = lazy(() => import('pages/PageStructureLevelTree'));
const PageCookies = lazy(() => import('pages/PageCookies'));
const RegisterForm = lazy(() => import('components/RegisterForm/RegisterForm'));
const UserDetail = lazy(() => import('components/Users/UserDetail/UserDetail'));
const SubscriptionDetail = lazy(() => import('components/Subscriptions/SubscriptionDetail/SubscriptionDetail'));
const PurchaseOrderDetail = lazy(() => import('components/PurchaseOrder/PurchaseOrderDetail/PurchaseOrderDetail'));

const PageTestButtons = lazy(() => import('pages/PageTestButtons'));

const SkeletonPage = () => (
  <>
    <Header />
    <SkeletonMain />
  </>
);

const PrivateRoute = ({
                        Component, allowedRoles = Object.values(APPLICATION_ROLES), isRolesNeeded = true, ...rest
                      }) => {
  const { isConnected } = userStore;
  if (CookiesHelper.getChangePasswordStatus()) {
    CookiesHelper.setChangePasswordCookie('false');
    const keycloak = KeyCloakUtils.currentKeycloakInstance();
    keycloak.login();
  }

  // Updated to use Navigate instead of Redirect
  return isConnected && ((isRolesNeeded && UserHelper.hasAccessRight(allowedRoles)) || !isRolesNeeded)
      ? <Component {...rest} />
      : <Navigate replace to={ROUTES.HOME} />;
};

const UnauthenticatedRoute = ({ Component, ...rest }) => {
  const { isConnected } = userStore;

  // Updated to use Navigate instead of Redirect
  return isConnected
      ? <Navigate replace to={ROUTES.HOME} />
      : <Component {...rest} />;
};

PrivateRoute.propTypes = {
  Component: PropTypes.shape({}).isRequired
};

export const Routes = () => (
  <Suspense fallback={SkeletonPage()}>
    <RouterRoutes>
      <Route element={<PrivateRoute Component={UserDetail} isRolesNeeded={false} />} path={`${ROUTES.USER_DETAIL}`} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={SubscriptionDetail} />} path={`${ROUTES.SUBSCRIPTION_DETAIL}`} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PurchaseOrderDetail} />} path={`${ROUTES.PURCHASE_ORDER_DETAIL}`} />

      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageAdministration} />} path={`${ROUTES.GROUP_LIST}`} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageAdministration} />} path={`${ROUTES.COMPANY_LIST}`} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageAdministration} />} path={`${ROUTES.INSTITUTION_LIST}`} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageAdministration} />} path={`${ROUTES.USER_LIST}`} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageAdministration} />} path={`${ROUTES.SUBSCRIPTION_LIST}`} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageAdministration} />} path={`${ROUTES.PURCHASE_ORDER_LIST}`} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageAdministration} />} path={ROUTES.ADMIN} />

      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageStructureLevelTree} />} path={ROUTES.COMPANY_TREE_PARAMS} />

      <Route element={<PrivateRoute allowedRoles={TRANSLATION_ROLES} Component={PageAdministrationTranslations} />} path={ROUTES.ADMIN_TRANSLATIONS} />
      <Route element={<PrivateRoute allowedRoles={TRANSLATION_ROLES} Component={PageAdministrationTranslationList} />} path={ROUTES.ADMIN_TRANSLATION_LIST_PARAMS} />

      <Route element={<PrivateRoute allowedRoles={TRANSLATION_ROLES} Component={PageAdministrationLanguages} />} path={ROUTES.ADMIN_LANGUAGES} />

      <Route element={<PrivateRoute allowedRoles={QUIZ_EDITION_ROLES} Component={PageAdministrationQuiz} />} path={ROUTES.ADMIN_QUIZ} />
      <Route element={<PrivateRoute allowedRoles={QUIZ_EDITION_ROLES} Component={PageAdministrationQuiz} />} path={ROUTES.ADMIN_RESULTS} />
      <Route element={<PrivateRoute allowedRoles={QUIZ_EDITION_ROLES} Component={PageAdministrationQuiz} />} path={ROUTES.ADMIN_QUESTION} />
      <Route element={<PrivateRoute allowedRoles={QUIZ_EDITION_ROLES} Component={PageAdministrationQuiz} />} path={ROUTES.ADMIN_CATEGORIES} />

      <Route element={<PrivateRoute allowedRoles={QUIZ_EDITION_ROLES} Component={PageQuiz} />} path={ROUTES.ADMIN_QUIZ_CREATION} />
      <Route element={<PrivateRoute allowedRoles={QUIZ_EDITION_ROLES} Component={PageQuiz} />} path={ROUTES.ADMIN_QUIZ_DETAIL} />

      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageAdministrationCountry} />} path={ROUTES.ADMIN_COUNTRIES} />
      <Route element={<PrivateRoute allowedRoles={VALIDATOR_ROLES} Component={PageCountry} />} path={ROUTES.ADMIN_COUNTRIES_DETAIL_PARAMS} />

      <Route element={<PrivateRoute allowedRoles={QUIZ_EDITION_ROLES} Component={PageQuizResults} />} path={ROUTES.ADMIN_QUIZ_RESULTS_DETAIL} />

      <Route element={<UnauthenticatedRoute Component={RegisterForm} />} path={ROUTES.REGISTER} />

      <Route element={<PageRGPD />} path={ROUTES.RGPD} />
      <Route element={<PageCookies />} path={ROUTES.COOKIES} />

      <Route element={<PageTestButtons />} path={ROUTES.TEST_BUTTONS} />
      <Route element={<PageHome />} path={ROUTES.HOME} />

      <Route element={<Navigate replace to={ROUTES.HOME} />} path="*" />
    </RouterRoutes>
  </Suspense>
);
